<script lang="ts">
	import type { FoodMenuChoiceIssueReport } from "$lib/def/food-menu";
	import type { CartItem, MenuItem, SelectionValue } from "$lib/ui/home/food/menu/server";
	import type { Subject } from "rxjs";
	
	import { getSelectedVariant } from "$lib/ui/home/food/menu/server";
	import { formatCurrency } from "$lib/util/format";
	import { createEventDispatcher } from "svelte";
	import { fly } from "svelte/transition";
	
	import Radio from "$lib/ui/form/Radio.svelte";
	import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
	import RestaurantMenuChoices from "./RestaurantMenuChoices.svelte";

  //###########################################################################################
  // This component displays all there is about a food menu item. The item image, name,
  // description and all its variants, options, extras and addons.
  //
  // Responsibilities include:
  // * It communicates selection updates to the parent component.
  // * It does not get the qoutes, nor captures the quantity.
  // * It manages the focusing of invalid selections ultimately dictated by the parent.
  //###########################################################################################

	const dispatch = createEventDispatcher();

	export let menuItem: MenuItem;
	export let cartItem: CartItem;

  // export let price: number;
  // export let updatingPrice: boolean;
	export let pricePromise: Promise<{price: number}>;

  export let reporter: Subject<FoodMenuChoiceIssueReport>;

  function select(key: string, value: number | SelectionValue) {
    dispatch("selection", { key, value });
  }
</script>

<div class="flex align-stretch margin-top">
  <button class="pdp-back-btn" on:click={() => dispatch("back")}>
    <span class="icon-chevron-left-20" />Back
  </button>
	<div class="fill-space">
		<div class="sticky" transition:fly>
			<!-- {#if preselect && prevQuote}
				Cart price adjustment {formatCurrency(price - prevQuote)}
			{/if} -->

			{#if menuItem.media}
				<div class="card square" style="margin-bottom: 16px;">
					<LazyBackgroundImage image={[menuItem.media, 400]} alt={menuItem.name}>
						{#if menuItem.availability?.status === 'sold_out'}
							<article class="cover">
								<span class="label-s bold nowrap">Sold out</span>
							</article>
						{/if}
					</LazyBackgroundImage>
				</div>
			{/if}
			{#if pricePromise && cartItem.variant_id}
				{#await pricePromise}
					<sup class="vertical-middle">updating...</sup>
				{:then quote}
					{#if quote}
						{@const variant = getSelectedVariant(menuItem.variants, cartItem.variant_id)}
						{#if variant?.old_price}
							<span class="headline-s bold red vertical-middle">{formatCurrency(quote.price)}</span>
							<span>&nbsp;</span>
							<s class="headline-s bold vertical-middle">{formatCurrency(variant.old_price)}</s>
						{:else}
							<span class="headline-s bold vertical-middle">{formatCurrency(quote.price)}</span>
							<!-- {:else}<p class="label-m book red">Item or selection is invalid.</p> -->
						{/if}
					{/if}
				{/await}
			{/if}
			<!-- {#if updatingPrice}<sup class="vertical-middle">updating...</sup>{/if} -->
			{#if menuItem.collect_only}
				<span class="pill small label-s bold m-0" style="position: relative; bottom: -2px;">Collect only</span>
			{/if}
			<h2 class="display-s bold">{menuItem.name}</h2>
      {#if menuItem.menu_deal_action === "buy_1_get_1_free"}
        <span class="label-xs bold promo-pill">BUY 1 GET 1 FREE</span>
      {/if}
			{#if menuItem.description}
				<sup class="body-m book block">{menuItem.description}</sup>
			{/if}

		</div>
	</div>

	<div class="fill-space">
		{#if menuItem.variants?.length}
			<div class="variants" in:fly|global={{ x: -40 }}>
				<h6 class="body-l bold">Choose type</h6>
				<div class="list">
					{#each menuItem.variants as variant}
						{@const name = menuItem.variants.length <= 1 ? menuItem.name : variant.name}
						<!--
							=================================================================
							TODO: name constant should reflect real name if
							- single variant
							- does not equal default "-----"
							=================================================================
						-->
						<Radio name={`variant-${menuItem.id}`} bind:group={cartItem.variant_id} onChange={() => select("variant_id", variant.id)} value={variant.id} disabled={menuItem.availability?.status === "sold_out" || variant.availability?.status === "sold_out"}>
							<div class="flex flex-nowrap">
								<div>{name}</div>
                {#if variant.availability?.status === "sold_out"}
                  <div class="bold">SOLD OUT</div>
                {:else}
					        <div class="bold">{formatCurrency(variant.price)}</div>
                {/if}
							</div>
						</Radio>
					{/each}
				</div>
			</div>
		{/if}

		{#if cartItem.variant_id}
			{@const variant = getSelectedVariant(menuItem.variants, cartItem.variant_id)}
			{#if variant?.options}
				{@const selectedGroups = cartItem.options?.filter(s => variant.options.map(o => o.id).includes(s.id)) ?? []}
				<RestaurantMenuChoices groups={variant.options} {selectedGroups} hostIndex="o" on:update={e => select('options', e.detail)} {reporter} />
			{/if}
			{#if variant?.extras}
			{@const selectedGroups = cartItem.extras?.filter(s => variant.extras.map(o => o.id).includes(s.id)) ?? []}
				<RestaurantMenuChoices groups={variant.extras} {selectedGroups} hostIndex="e" on:update={e => select('extras', e.detail)}  {reporter} hostPosition={variant?.options ? 1 : 0} />
			{/if}
			{#if variant?.addons}
			{@const selectedGroups = cartItem.addons?.filter(s => variant.addons.map(o => o.id).includes(s.id)) ?? []}
				<RestaurantMenuChoices groups={variant.addons} {selectedGroups} hostIndex="a" on:update={e => select('addons', e.detail)}  {reporter} hostPosition={variant?.options && variant?.extras ? 2 : (variant?.options || variant?.extras) ? 1 : 0} />
			{/if}
		{/if}
	</div>
</div>

<style lang="scss">
  @use "screen";
  @use "color";

  .margin-top {
    margin-top: 1rem;

    @media (screen.$max-xl) {
      margin-top: 2rem;
    }

    @media (screen.$max-lg) {
      margin-top: 3rem;
    }

    @media (screen.$max-md) {
      margin-top: 3.5rem;
    }
  }
</style>
